import type { OnInit, ElementRef, QueryList } from '@angular/core';
import type { SelectedFileExtended } from '@common/types/file.type';
import type { CarouselOptions, CollageOptions, GalleryWidgetData } from '@web/widgets/gallery/type/gallery-widget.type';
import type { ResizeEvent } from 'angular-resizable-element';
import type { SwiperOptions } from 'swiper';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, NgZone, ViewChild, ViewChildren } from '@angular/core';
import { EGalleryTypes } from '@common/enums';
import { ResizableModule } from 'angular-resizable-element';
import { filter, takeUntil } from 'rxjs';
import Swiper, { Autoplay, EffectCards, EffectFade, FreeMode, Navigation, Pagination, Thumbs } from 'swiper';
import { SwiperModule } from 'swiper/angular';

import { WebBaseViewComponent } from '@web/widgets/common/abstract/web-base-view.component';
import { BLANK_IMAGE } from '@web/widgets/image/constants/image.consts';

import { getStyles } from './gallery-view.component.styles';
import { environment } from '@web-env/environment';
import { SppFallbackImgDirective } from '@common/directives/fallback-img.directive';
import { formatFileUrl, formatFileUrlWithResize } from '@components/sp-file-manager/src/helpers';

Swiper.use([Autoplay, Pagination, Thumbs, Navigation, EffectCards, EffectFade, FreeMode]);

@Component({
    selector: 'web-gallery-view',
    templateUrl: './gallery-view.component.html',
    styleUrls: ['./gallery-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, SwiperModule, ResizableModule, SppFallbackImgDirective],
})
export class GalleryViewComponent extends WebBaseViewComponent<GalleryWidgetData> implements OnInit {
    @ViewChildren('resizeBox') resizeBox: QueryList<ElementRef<HTMLElement>>;
    @ViewChildren('resizeThumbBox') resizeThumbBox: QueryList<ElementRef<HTMLElement>>;
    @ViewChild('dragHandleBottom') dragHandleBottom: ElementRef;
    @ViewChildren('overlayImageRef') overlayImageRefs: QueryList<ElementRef<HTMLElement>>;
    @ViewChildren('imageRef') imageRefs: QueryList<ElementRef<HTMLElement>>;

    private ngZone: NgZone = inject(NgZone);

    protected getStyles = getStyles;

    mainSwiper: Swiper;
    thumbsSwiper: Swiper;
    swiperConfig: SwiperOptions = {};
    thumbsParentConfig: SwiperOptions = {};

    // swiper/angular v8 не вміє надійно переініціалізувати navigation у рантаймі,
    // тож при зміні isOutsideContainerArrow перестворюємо swiper цілком
    public renderDefaultSwiper = true;
    private prevOutsideArrow: boolean;

    // поточний режим конструктора — потрібен для ресайзу thumbs по девайсах
    private isMobileView = false;
    private isTabletView = false;

    BLANK_IMAGE = BLANK_IMAGE;

    public get loopedSlides(): number {
        return this.images.length === this.carouselOptions.rowItems ? this.images.length : this.images.length - 1;
    }

    public get isMasonry(): boolean {
        if ('style' in this.widgetData.options) {
            return ['masonry', '2col-2row'].includes(this.widgetData.options.style);
        }
        return false;
    }

    public get isCarousel(): boolean {
        return this.widgetData?.type === EGalleryTypes.carousel;
    }

    public get carouselOptions(): CarouselOptions {
        return this.widgetData?.options as CarouselOptions;
    }

    public get prevArrowClass(): string {
        return `swiper-button-prev-sp-custom-${this.widget?.id}`;
    }

    public get nextArrowClass(): string {
        return `swiper-button-next-sp-custom-${this.widget?.id}`;
    }

    public get navigationConfig(): SwiperOptions['navigation'] {
        return this.carouselOptions?.isOutsideContainerArrow && this.carouselOptions?.type === 'default'
            ? { nextEl: `.${this.nextArrowClass}`, prevEl: `.${this.prevArrowClass}` }
            : true;
    }

    public get masonryArrays(): SelectedFileExtended[][] {
        const columns = (this.widgetData.options as CollageOptions).style === '2col-2row' ? 2 : 3;
        const arr = [...Array(columns)].map(() => []);

        for (let i = 0; i < this.images.length; i++) {
            arr[i % columns].push(this.images[i]);
        }

        return arr;
    }

    public get images() {
        return this.widgetData.images?.filter(Boolean) || [];
    }

    public isHasBottomSpace(): boolean {
        if (this.images.length <= this.carouselOptions.rowItems) {
            return false;
        }

        return true;
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.isDeviceMobile$.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => (this.isMobileView = value));
        this.isDeviceTablet$.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => (this.isTabletView = value));

        this.widget$.pipe(takeUntil(this.unsubscribe$), filter(Boolean)).subscribe((widget) => {
            const widgetData = widget.data as GalleryWidgetData;
            if (widgetData.type !== EGalleryTypes.carousel) return;

            const { type, autoplay, isOutsideContainerArrow } = widgetData.options as CarouselOptions;

            if (type === 'default' && this.thumbsSwiper) {
                this.thumbsSwiper.destroy();
                this.thumbsSwiper = null;
            }

            // перестворюємо default-swiper при зміні isOutsideContainerArrow,
            // щоб navigation проініціалізувалася з нуля з правильними кнопками
            const outside = !!isOutsideContainerArrow;
            if (type === 'default' && this.prevOutsideArrow !== undefined && this.prevOutsideArrow !== outside) {
                this.prevOutsideArrow = outside;
                this.renderDefaultSwiper = false;
                this.changeDetectorRef.detectChanges();
                this.renderDefaultSwiper = true;
                this.changeDetectorRef.detectChanges();
                return;
            }
            this.prevOutsideArrow = outside;

            if (autoplay && this.mainSwiper) {
                this.swiperConfig.autoplay = this.thumbsParentConfig.autoplay = {
                    delay: 2000,
                    pauseOnMouseEnter: true,
                    disableOnInteraction: false,
                };

                this.mainSwiper?.autoplay?.start();
            } else {
                this.swiperConfig.autoplay = this.thumbsParentConfig.autoplay = {};
                this.mainSwiper?.autoplay?.stop();
            }

            this.changeDetectorRef.detectChanges();
        });
    }

    instanceId = (index: number, instance: any): number => index;

    formatImageURL(image: SelectedFileExtended): string {
        return formatFileUrlWithResize(image, environment.fileManagerCdnUrl, 720);
    }

    formatImageFallbackURL(image: SelectedFileExtended): string {
        return formatFileUrl(image, environment.fileManagerCdnUrl);
    }

    public overlayImageSrc(image: SelectedFileExtended): boolean | string {
        if (!image.overlayImage) {
            return false;
        }

        return formatFileUrl(image.overlayImage, environment.fileManagerCdnUrl);
    }

    setSwiper(swiper: Swiper) {
        this.mainSwiper = swiper;

        if (this.carouselOptions.autoplay) {
            this.swiperConfig = {
                autoplay: {
                    delay: 2000,
                    pauseOnMouseEnter: true,
                    disableOnInteraction: false,
                },
            };
            this.mainSwiper.autoplay?.start();
        }
    }

    setThumbs(swiper: Swiper) {
        this.thumbsSwiper = swiper;
        this.thumbsParentConfig = {
            thumbs: {
                swiper: swiper,
            },
        };

        this.changeDetectorRef.detectChanges();
    }

    // get resizeBoxElement(): HTMLElement {
    //     return this.resizeBox.nativeElement;
    // }

    // validateResize(event: ResizeEvent): boolean {
    //     return true;
    // }

    public getResizedImageHeight(height): number {
        if (height < 600) {
            return height / 2;
        } else if (height > 600) {
            return 600;
        } else {
            return height;
        }
    }

    // The sizes are read off the image once it loads, not set by the user: stored without a history entry
    // and without saving the page by themselves (#75695).
    private syncWidgetData(data: Partial<GalleryWidgetData>): void {
        this.widgetsFacade.syncLegacyWidgetProperties(this.widget.id, { data: data as GalleryWidgetData });
    }

    resizeOnload(any: HTMLImageElement, target: HTMLElement, index: number) {
        if (this.images[index].resizedHeight) return;
        const newPadding = (any.naturalHeight / any.naturalWidth) * 100;

        target.style.paddingTop = newPadding + '%';

        if (this.widgetData.type === EGalleryTypes.collage) {
            const { style } = this.widgetData.options as CollageOptions;
            const isMasonry = ['masonry', '2col-2row'].includes(style);
            if (isMasonry) {
                this.syncWidgetData({
                    images: this.images?.map((image, i) => {
                        if (i === index) {
                            return {
                                ...image,
                                resizedHeight: any.naturalHeight,
                                resizedWidth: any.naturalWidth,
                            };
                        }
                        return image;
                    }),
                });
                return;
            }
        }

        this.syncWidgetData({
            images: this.images?.map((image) => {
                return {
                    ...image,
                    resizedHeight: this.getResizedImageHeight(any.naturalHeight),
                    resizedWidth: any.naturalWidth,
                };
                return image;
            }),
        });

        if (this.widgetData?.resizedHeight) {
            target.style.paddingTop = this.widgetData?.resizedHeight + '%';
        }
    }

    onResizeStart(event: ResizeEvent, target: HTMLElement): void {}
    onResizing(event: ResizeEvent, target: HTMLElement): void {
        if (this.widgetData.type === EGalleryTypes.collage) {
            const { style } = this.widgetData.options as CollageOptions;
            const isMasonry = ['masonry', '2col-2row'].includes(style);
            if (isMasonry) {
                if (typeof event.edges.bottom !== 'number') return;
                if (event.rectangle.height <= 50) return;
                const newPadding = (event.rectangle.height / event.rectangle.width) * 100;
                target.style.paddingTop = newPadding + '%';
                return;
            }
        }

        this.resizeBox.forEach(({ nativeElement }) => {
            if (typeof event.edges.bottom !== 'number') return;
            if (event.rectangle.height <= 50) return;
            const newPadding = (event.rectangle.height / event.rectangle.width) * 100;
            nativeElement.style.paddingTop = newPadding + '%';
        });
    }
    onResizeEnd(event: ResizeEvent, target: HTMLElement, index: number): void {
        if (this.widgetData.type === EGalleryTypes.collage) {
            const { style } = this.widgetData.options as CollageOptions;
            const isMasonry = ['masonry', '2col-2row'].includes(style);
            if (isMasonry) {
                this.updateWidgetData({
                    images: this.images?.map((image, i) => {
                        if (i === index) {
                            return {
                                ...image,
                                resizedHeight: event.rectangle.height,
                                resizedWidth: event.rectangle.width,
                            };
                        }
                        return image;
                    }),
                });
                return;
            }
        }

        this.updateWidgetData({
            images: this.images?.map((image) => {
                return {
                    ...image,
                    resizedHeight: event.rectangle.height,
                    resizedWidth: event.rectangle.width,
                };
                return image;
            }),
        });

        if (this.widgetData.type === EGalleryTypes.grid || this.widgetData.type === EGalleryTypes.carousel) {
            this.updateWidgetData({
                resizedHeight: (event.rectangle.height / event.rectangle.width) * 100,
            });
        }
    }

    onResizeThumbStart(event: ResizeEvent, target: HTMLElement): void {}

    onResizingThumb(event: ResizeEvent, target: HTMLElement): void {
        if (typeof event.edges.bottom !== 'number') return;
        if (event.rectangle.height <= 50) return;

        this.resizeThumbBox.forEach(({ nativeElement }) => {
            nativeElement.style.maxHeight = event.rectangle.height + 'px';
        });
    }

    onResizeThumbEnd(event: ResizeEvent, target: HTMLElement): void {
        const height = event.rectangle.height < 50 ? 50 : event.rectangle.height;

        let heightOptions: Partial<CarouselOptions>;

        if (this.widgetData?.isSynchronizeStyle === false) {
            // стилі не синхронізовані — змінюємо висоту лише для активного режиму конструктора
            if (this.isMobileView) {
                heightOptions = { previewImageHeightMobile: height };
            } else if (this.isTabletView) {
                heightOptions = { previewImageHeightTablet: height };
            } else {
                heightOptions = { previewImageHeight: height };
            }
        } else {
            // стилі синхронізовані — виставляємо однакову висоту для всіх девайсів
            heightOptions = {
                previewImageHeight: height,
                previewImageHeightTablet: height,
                previewImageHeightMobile: height,
            };
        }

        this.updateWidgetData({
            options: {
                ...this.widgetData.options,
                ...heightOptions,
            } as CarouselOptions,
        });
    }

    public getThumbMaxHeight(): number {
        const options = this.carouselOptions;
        const desktopHeight = options?.previewImageHeight || 240;

        if (this.widgetData?.isSynchronizeStyle === false) {
            if (this.isMobileView) {
                return options?.previewImageHeightMobile ?? desktopHeight;
            }
            if (this.isTabletView) {
                return options?.previewImageHeightTablet ?? desktopHeight;
            }
        }

        return desktopHeight;
    }

    imagePosition = { x: 50, y: 50 };
    cursorPosBefore = { x: 0, y: 0 };
    imagePosBefore = null;
    imagePosAfter = null;
    isMouseDown = false;
    mouseUp(index: number): void {
        if (!this.isMouseDown) return;
        this.isMouseDown = false;
        this.updateWidgetData({
            images: this.images?.map((image, i) => {
                if (i === index) {
                    return {
                        ...image,
                        backgroundPositionX: this.imagePosAfter.x,
                        backgroundPositionY: this.imagePosAfter.y,
                    };
                }
                return image;
            }),
        });
    }

    mouseDown(event: MouseEvent, image: SelectedFileExtended): void {
        event.preventDefault();
        this.imagePosition = {
            x: image.backgroundPositionX || 50,
            y: image.backgroundPositionY || 50,
        };
        this.isMouseDown = true;
        this.imagePosAfter = this.imagePosition;
        this.imagePosBefore = this.imagePosAfter;
        this.cursorPosBefore = { x: event.pageX, y: event.pageY };
    }
    move(event: MouseEvent, imageRef: HTMLImageElement): void {
        event.preventDefault();
        if (event.buttons !== 1 || !this.isMouseDown) return;

        const newXPos = this.imagePosBefore.x + ((this.cursorPosBefore.x - event.clientX) / imageRef.width) * 100;
        const newYPos = this.imagePosBefore.y + ((this.cursorPosBefore.y - event.clientY) / imageRef.height) * 100;

        if (newXPos < 0 || newXPos > 100 || newYPos < 0 || newYPos > 100) {
            return;
        }
        this.imagePosAfter = { x: newXPos, y: newYPos };

        (event.target as HTMLImageElement).style.objectPosition = `${newXPos}% ${newYPos}%`;
    }

    public getImagePaddingTop(image: SelectedFileExtended): number {
        if (!image?.resizedHeight || !image?.resizedWidth) {
            return 60;
        }

        return (image.resizedHeight / image.resizedWidth) * 100;
    }

    public mouseImgIn(index: number) {
        const overlayImageRef = this.overlayImageRefs.toArray()[index];
        const imageRef = this.imageRefs.toArray()[index];

        if (!overlayImageRef || !imageRef) return;

        imageRef.nativeElement.style.opacity = '0';
        overlayImageRef.nativeElement.style.opacity = '1';
        this.changeDetectorRef.detectChanges();
    }

    public mouseImgOut(index: number) {
        const overlayImageRef = this.overlayImageRefs.toArray()[index];
        const imageRef = this.imageRefs.toArray()[index];

        if (!overlayImageRef || !imageRef) return;

        imageRef.nativeElement.style.opacity = '1';
        overlayImageRef.nativeElement.style.opacity = '0';
        this.changeDetectorRef.detectChanges();
    }

    public getSwiperBreakpoints(isMobile: boolean, isTablet: boolean): Record<number, any> {
        if (isMobile) {
            return {
                0: {
                    slidesPerView: this.carouselOptions.rowItemsMobile || this.carouselOptions.rowItems,
                    spaceBetween: this.carouselOptions.imageGapMobile || this.carouselOptions.imageGap,
                },
            };
        }

        if (isTablet) {
            return {
                0: {
                    slidesPerView: this.carouselOptions.rowItemsTablet || this.carouselOptions.rowItems,
                    spaceBetween: this.carouselOptions.imageGapTablet || this.carouselOptions.imageGap,
                },
            };
        }

        return {
            0: {
                slidesPerView: this.carouselOptions.rowItems || 1,
                spaceBetween: this.carouselOptions.imageGap || 0,
            },
        };
    }

    public getSwiperThumbsBreakpoints(isMobile: boolean, isTablet: boolean): Record<number, any> {
        if (isMobile) {
            return {
                0: {
                    slidesPerView:
                        this.thumbsEdgeTransform(this.carouselOptions.rowItemsMobile as number) ||
                        this.thumbsEdgeTransform(this.carouselOptions.rowItems),
                    spaceBetween: this.carouselOptions.imageGapMobile || this.carouselOptions.imageGap,
                },
            };
        }

        if (isTablet) {
            return {
                0: {
                    slidesPerView:
                        this.thumbsEdgeTransform(this.carouselOptions?.rowItemsTablet as number) ||
                        this.thumbsEdgeTransform(this.carouselOptions.rowItems),
                    spaceBetween: this.carouselOptions.imageGapTablet || this.carouselOptions.imageGap,
                },
            };
        }

        return {
            0: {
                slidesPerView: this.thumbsEdgeTransform(this.carouselOptions.rowItems) || 1,
                spaceBetween: this.carouselOptions.imageGap || 0,
            },
        };
    }

    public thumbsEdgeTransform(rowItems: number): number {
        if (this.carouselOptions?.isShowLastPreviewEdge) {
            return rowItems + 0.2;
        }
        return rowItems;
    }
}
